/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\aiAgentsCrudService.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/


import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { AiAgent, transformToApiFormat } from './aiAgentsTypes';

const BASE_PATH = '/api/telephony/ai-agents';

export async function getAiAgents(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    agentType?: string;
}): Promise<{ data: AiAgent[]; meta: { total: number } }> {
    const q = new URLSearchParams();
    if (params?.agentType) q.append('agentType', params.agentType);
    if (params?.page) q.append('page', params.page.toString());
    if (params?.limit) q.append('limit', params.limit.toString());
    if (params?.sortBy) q.append('sortBy', params.sortBy);
    if (params?.sortOrder) q.append('sortOrder', params.sortOrder);
    if (params?.search) q.append('search', params.search);
    const res = await fetch(`${buildApiUrl(BASE_PATH)}?${q}`, { method: 'GET', headers: getApiHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
}

export async function getAiAgentById(id: number | string): Promise<AiAgent> {
    const res = await fetch(buildApiUrl(`${BASE_PATH}/${id}`), { method: 'GET', headers: getApiHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
}

export async function createAiAgent(data: Partial<AiAgent>): Promise<AiAgent> {
    const res = await fetch(buildApiUrl(BASE_PATH), { method: 'POST', headers: getApiHeaders(), body: JSON.stringify(transformToApiFormat(data as Record<string, any>)) });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || `HTTP ${res.status}`); }
    return res.json();
}

export async function updateAiAgent(id: number | string, data: Partial<AiAgent>): Promise<AiAgent> {
    const res = await fetch(buildApiUrl(`${BASE_PATH}/${id}`), { method: 'PUT', headers: getApiHeaders(), body: JSON.stringify(transformToApiFormat(data as Record<string, any>)) });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || `HTTP ${res.status}`); }
    return res.json();
}

export async function deleteAiAgent(id: number | string): Promise<void> {
    const res = await fetch(buildApiUrl(`${BASE_PATH}/${id}`), { method: 'DELETE', headers: getApiHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
}
